// src/containers/reportBuilder/CustomReportSlideout.jsx
//
// Embeds the Report Builder for a single, already-known activity inside a
// SlideoutPanel — e.g. the "Custom Report" button on the Activity page's
// Report tab. Unlike the admin Report Builder page, there is no filter
// panel: the activity is fixed, so the "Report" tab is always the preview.
//
// Fetches the one activity it needs directly (getReportBuilderActivity —
// GET /report-builder/activity?activityId=) rather than reshaping the
// Activity page's own `currentActivity` object into the Report Builder's
// activity shape — the two have different fields (see takso-web CLAUDE.md) —
// or fetching the whole organisation's activity list just to find one by id.

import { useEffect, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { SlideoutPanel } from '../../components';
import ReportBuilderPanel from './ReportBuilderPanel';
import ReportPreviewSection from './ReportPreviewSection';
import useReportActivityData from './useReportActivityData';
import {
  getAllOrganisations,
  getReportBuilderActivity,
} from '../../store/actions';

function CustomReportSlideout({ t, open, onClose, activityId, orgId }) {
  const dispatch = useDispatch();
  const activity = useSelector((s) => s.reportBuilder.activity);
  const loading = useSelector((s) => s.reportBuilder.activityLoading);
  const orgList = useSelector((s) =>
    Array.isArray(s.admin.organisationsAll) ? s.admin.organisationsAll : []
  );

  useEffect(() => {
    if (!open || !activityId) return;
    dispatch(getAllOrganisations());
    dispatch(getReportBuilderActivity(activityId));
  }, [open, activityId, dispatch]);

  const resolvedActivity =
    activity && activity.id === activityId ? activity : null;
  const selectedActivities = useMemo(
    () => (resolvedActivity ? [resolvedActivity] : []),
    [resolvedActivity]
  );
  const orgName = orgList.find((o) => o.id === orgId)?.name || '';

  const activityData = useReportActivityData(selectedActivities);

  return (
    <SlideoutPanel
      open={open}
      onClose={onClose}
      title={t('report.builder.title')}
      wide="xxwide"
    >
      {!resolvedActivity ? (
        <div className="rb-empty rb-empty--centered">
          {loading
            ? `${t('report.builder.loading_activity')}…`
            : t('report.builder.activity_not_found')}
        </div>
      ) : (
        <ReportBuilderPanel
          orgId={orgId}
          orgName={orgName}
          selectedActivities={selectedActivities}
          evaluationPlans={activityData.evaluationPlans}
          chartImages={activityData.chartImages}
          onChartImageChange={activityData.handleChartImageChange}
          hideChartsTab
          renderReportTab={({
            prefs,
            includedCharts,
            chartSettings,
            assistPrefs,
            reportAssistEnabled,
          }) => (
            <ReportPreviewSection
              selectedActivities={selectedActivities}
              orgId={orgId}
              orgName={orgName}
              prefs={prefs}
              includedCharts={includedCharts}
              chartSettings={chartSettings}
              assistPrefs={assistPrefs}
              reportAssistEnabled={reportAssistEnabled}
              venues={activityData.venues}
              objectives={activityData.objectives}
              crossRefs={activityData.crossRefs}
              fastFacts={activityData.fastFacts}
              evaluationPlans={activityData.evaluationPlans}
              chartImages={activityData.chartImages}
              shortlistedComments={activityData.shortlistedComments}
              surveyResponseComments={activityData.surveyResponseComments}
              // No onEdit/onClear — the activity is fixed, there's nothing
              // to go back to a selection screen for.
            />
          )}
        />
      )}
    </SlideoutPanel>
  );
}

CustomReportSlideout = withTranslation()(CustomReportSlideout);

export default CustomReportSlideout;
